// ts/components/theme-toggle.ts

type Theme = "light" | "dark";
const THEME_STORAGE_KEY = "theme";
const ACTIVE_SWITCH_CLASS = "is-active"; // CSS class to apply to the button when "on" (e.g., dark mode)

// Function to apply the theme and update the switch's ARIA state and visual class
function applyTheme(theme: Theme, switchElement: HTMLButtonElement | null): void {
  document.documentElement.setAttribute("data-theme", theme);
  localStorage.setItem(THEME_STORAGE_KEY, theme);

  if (switchElement) {
    if (theme === "dark") {
      switchElement.querySelector(".button_toggle-bg").classList.add(ACTIVE_SWITCH_CLASS);
      switchElement.querySelector(".button_toggle-knob").classList.add(ACTIVE_SWITCH_CLASS);
      switchElement.setAttribute("aria-checked", "true");
      // Note: The visible label "Dark Mode" is static in your HTML.
      // If you wanted to change it (e.g., "Light Mode On"), you'd do it here:
      // const labelElement = document.getElementById('theme-toggle-label');
      // if (labelElement) labelElement.textContent = "Light Mode is On";
    } else {
      switchElement.querySelector(".button_toggle-bg").classList.remove(ACTIVE_SWITCH_CLASS);
      switchElement.querySelector(".button_toggle-knob").classList.remove(ACTIVE_SWITCH_CLASS);
      switchElement.setAttribute("aria-checked", "false");
      // const labelElement = document.getElementById('theme-toggle-label');
      // if (labelElement) labelElement.textContent = "Dark Mode is On";
    }
  }
}

// Main initialization function for the theme toggler
export function initThemeToggler(switchId: string = "theme-toggle-switch"): void {
  // Defaulting to your new ID
  const themeSwitch = document.getElementById(switchId) as HTMLButtonElement | null;

  if (!themeSwitch) {
    console.warn(`Theme switch button with ID "${switchId}" not found.`);
    return;
  }

  // The role="switch" and aria-labelledby should be set in the HTML.
  // We ensure aria-checked is correctly initialized by applyTheme.

  const currentTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
  const prefersDarkScheme = window.matchMedia("(prefers-color-scheme: dark)");

  // Determine and apply initial theme
  let initialTheme: Theme = "light"; // Default to light
  if (currentTheme) {
    initialTheme = currentTheme;
  } else if (prefersDarkScheme.matches) {
    initialTheme = "dark";
  }
  // Set initial aria-checked state and class by applying the theme
  applyTheme(initialTheme, themeSwitch);

  // Listen for system preference changes (e.g., OS theme changes)
  prefersDarkScheme.addEventListener("change", (e) => {
    // Only apply if user hasn't manually set a theme via localStorage
    if (!localStorage.getItem(THEME_STORAGE_KEY)) {
      applyTheme(e.matches ? "dark" : "light", themeSwitch);
    }
  });

  // Toggle switch event listener
  themeSwitch.addEventListener("click", () => {
    // No event.preventDefault() needed for <button type="button">
    const currentThemeOnElement = document.documentElement.getAttribute("data-theme");
    const newTheme: Theme = currentThemeOnElement === "dark" ? "light" : "dark";
    applyTheme(newTheme, themeSwitch);
  });
}
